// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Mail from "@insite/mobius/Icons/Mail";
import Phone from "@insite/mobius/Icons/Phone";
import { Button, Clickable, GridContainer, GridItem, TextField } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Edit from "@werner/XDL/Icons/Edit";
import { mergeWithCss, modalButtonsActionStyles, xMask } from "@werner/XDL/Utilities";
import { createLinkIconStyles } from "@werner/XDL/Utilities/sharedStyles";
import * as React from "react";
import { css } from "styled-components";

export interface AddressInfoDisplayStyles {
    wrapper?: InjectableCss;
    customerNameText?: TypographyProps;
    firstNameText?: TypographyProps;
    lastNameText?: TypographyProps;
    attentionText?: TypographyProps;
    companyNameText?: TypographyProps;
    address1Text?: TypographyProps;
    address2Text?: TypographyProps;
    address3Text?: TypographyProps;
    address4Text?: TypographyProps;
    cityStatePostalCodeText?: TypographyProps;
    cityText?: TypographyProps;
    stateText?: TypographyProps;
    postalCodeText?: TypographyProps;
    countryText?: TypographyProps;
    phoneText?: LinkPresentationProps;
    emailText?: LinkPresentationProps;
    faxText?: TypographyProps;
    // XNG-Changes
    customerNumberText?: TypographyProps;
}

const commonTextStyles: TypographyProps = {
    as: "p",
    size: 15,
    weight: 400,
    color: "text.brand2",
    css: css`
        letter-spacing: 0;
        line-height: 17px;
    `,
};

export const addressInfoDisplayStyles: AddressInfoDisplayStyles = {
    wrapper: {
        css: css`
            font-style: normal;
            display: flex;
            flex-direction: column;
            @media print {
                display: block;
            }
        `,
    },
    customerNameText: {
        css: css`
            display: flex;
            align-items: center;
            @media print {
                font-size: 12px;
            }
        `,
    },
    attentionText: {
        ...commonTextStyles,
    },
    companyNameText: {
        ...commonTextStyles,
        weight: 500,
        css: css`
            margin-bottom: 4px;
        `,
    },
    address1Text: {
        ...commonTextStyles,
    },
    address2Text: {
        ...commonTextStyles,
    },
    address3Text: {
        ...commonTextStyles,
    },
    address4Text: {
        ...commonTextStyles,
    },
    cityStatePostalCodeText: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    countryText: {
        ...commonTextStyles,
    },
    phoneText: {
        ...createLinkIconStyles(Phone, {
            color: "secondary",
            css: css``,
        }),
    },
    emailText: {
        ...createLinkIconStyles(Mail, {
            color: "text",
            css: css``,
        }),
    },
    faxText: {
        ...commonTextStyles,
    },
    cityText: {
        ...commonTextStyles,
    },
    stateText: {
        ...commonTextStyles,
    },
    postalCodeText: {
        ...commonTextStyles,
    },
    firstNameText: {
        ...commonTextStyles,
    },
    lastNameText: {
        ...commonTextStyles,
    },
    customerNumberText: {
        css: css`
            @media print {
                font-size: 12px;
            }
            &:before {
                content: "#";
            }
        `,
    },
};

interface OwnProps {
    firstName?: string;
    lastName?: string;
    companyName?: string;
    attention?: string;
    address1: string;
    address2?: string;
    address3?: string;
    address4?: string;
    city: string;
    state?: string;
    postalCode: string;
    country?: string;
    phone?: string;
    fax?: string;
    email?: string;
    extendedStyles?: AddressInfoDisplayStyles;
    /* XNGAGE Changes */
    customerNumber?: string;
}

type Props = OwnProps;

// <XNG-Changes>
export type XngAddressInfoDisplayProps = Omit<Props, "extendedStyles">;
// </XNG-Changes>

const StyledAddress = getStyledWrapper("address");

const AddressInfoDisplay: React.FunctionComponent<Props> = function Address(props: Props) {
    const [styles] = React.useState(() => mergeToNew(addressInfoDisplayStyles, props.extendedStyles));
    return (
        <StyledAddress {...styles.wrapper} data-test-selector="addressInfoDisplay">
            {/* XNG-Changes: add customerNumber */}
            {/* XNG-Changes: make companyname at top */}
            {props.companyName && (
                <Typography {...styles.companyNameText} data-test-selector="addressInfoDisplay_companyName">
                    {props.companyName}{" "}
                    {props.customerNumber && (
                        <Typography
                            {...styles.customerNumberText}
                            data-test-selector="addressInfoDisplay_customerNumber"
                        >
                            {props.customerNumber}
                        </Typography>
                    )}
                </Typography>
            )}
            {/* <XNG-Changes>: change position from top */}

            <FullName
                firstName={props.firstName}
                lastName={props.lastName}
                styles={styles}
                data-test-selector="addressInfoDisplay_fulName"
            />
            {/* Attention is mapped to Address Nickname, we don't want to show it here */}
            {/* {props.attention && (
                <Typography {...styles.attentionText} data-test-selector="addressInfoDisplay_attention">
                    {translate("Attn:")} {props.attention}
                </Typography>
            )} */}

            {/* </XNG-Changes> */}
            <Typography {...styles.address1Text} data-test-selector="addressInfoDisplay_address1">
                {props.address1}
            </Typography>
            {props.address2 && (
                <Typography {...styles.address2Text} data-test-selector="addressInfoDisplay_address2">
                    {props.address2}
                </Typography>
            )}
            {props.address3 && (
                <Typography {...styles.address3Text} data-test-selector="addressInfoDisplay_address3">
                    {props.address3}
                </Typography>
            )}
            {props.address4 && (
                <Typography {...styles.address4Text} data-test-selector="addressInfoDisplay_address4">
                    {props.address4}
                </Typography>
            )}
            {/* <XNG-Changes> swipe places */}
            {props.country && (
                <Typography {...styles.countryText} data-test-selector="addressInfoDisplay_country">
                    {props.country}
                </Typography>
            )}
            <CityStatePostalCode city={props.city} state={props.state} postalCode={props.postalCode} styles={styles} />
            {/* </XNG-Changes> */}
            {props.fax && (
                <Typography {...styles.faxText} data-test-selector="addressInfoDisplay_fax">
                    {props.fax}
                </Typography>
            )}
            {props.phone && (
                <Link
                    href={`tel:${props.email}`}
                    {...styles.phoneText}
                    data-test-selector="addressInfoDisplay_phoneNumber"
                    aria-label={props.phone}
                >
                    {xMask.phoneNumber(props.phone)}
                </Link>
            )}
            {props.email && (
                <Link
                    href={`mailto:${props.email}`}
                    {...styles.emailText}
                    data-test-selector="addressInfoDisplay_emailAddress"
                    aria-label={props.email}
                >
                    {props.email}
                </Link>
            )}
        </StyledAddress>
    );
};

interface FullNameProps {
    firstName?: string;
    lastName?: string;
    styles: AddressInfoDisplayStyles;
}

const FullName = ({ firstName, lastName, styles }: FullNameProps) => {
    const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
    if (!fullName) {
        return null;
    }
    return (
        <Typography {...styles.customerNameText}>
            {firstName && (
                <Typography {...styles.firstNameText} data-test-selector="addressInfoDisplay_firstName">
                    {`${firstName} `}
                </Typography>
            )}
            {lastName && (
                <>
                    &nbsp;
                    <Typography {...styles.lastNameText} data-test-selector="addressInfoDisplay_lastName">
                        {lastName}
                    </Typography>
                </>
            )}
        </Typography>
    );
};

interface CityStatePostalCodeProps {
    city: string;
    state?: string;
    postalCode: string;
    styles: AddressInfoDisplayStyles;
}

const CityStatePostalCode = ({ city, state, postalCode, styles }: CityStatePostalCodeProps) => {
    return (
        <Typography {...styles.cityStatePostalCodeText}>
            <Typography {...styles.cityText} data-test-selector="addressInfoDisplay_city">
                {city}
            </Typography>
            {state && (
                <>
                    {", "}&nbsp;
                    <Typography {...styles.stateText} data-test-selector="addressInfoDisplay_state">
                        {state}
                    </Typography>
                </>
            )}
            {postalCode && (
                <>
                    &nbsp;
                    <Typography {...styles.postalCodeText} data-test-selector="addressInfoDisplay_postalCode">
                        {postalCode}
                    </Typography>
                </>
            )}
        </Typography>
    );
};

export default AddressInfoDisplay;
